import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { ApiInternal } from "src/api/api_internal";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { Description, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import React, { useEffect } from "react";
import { UUID } from "src/api/api_common";

/**
 * Props for `RecreateInvitationDialog`
 */
export type RecreateInvitationDialogProps = {
    /** User UUID of the user the invitation should be created for */
    user: UUID;
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the popup is submitted */
    onSubmit: () => void;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to recreate an invitation
 */
export default function RecreateInvitationDialog(props: RecreateInvitationDialogProps) {
    const [t] = useTranslation("dialog-recreate-invitation");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            link_valid_until: 1,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                await ApiInternal.users.recreateInvitation(props.user, { link_valid_until: value.link_valid_until });
                props.onSubmit();
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.user]);

    return (
        <Dialog size={"md"} open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.recreate-invitation")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"link_valid_until"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.link-valid-until")}</RequiredLabel>
                                        <Description>{t("description.link-valid-until")}</Description>
                                        <Input
                                            type={"number"}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            min={1}
                                            max={255}
                                            onChange={(e) => fieldApi.handleChange(Number(e.target.value))}
                                        />
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.recreate-invitation")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
